import { produce } from 'immer';
import React, { useState } from 'react';
import { RSButton, RSCheckbox } from 'reactsymbols-kit';
import { useAsynchronousAction } from '../../../hooks';
import Styles from '../../../styles/colors';
import { SpecialisedEventActionSelector } from './DeleteInheritanceForm';
import type { IModelVersion } from '../../../stores/projects/model_version';
import type { IModel } from '../../../stores/model';
import { asRSChange, rsValue, type RSCheckboxChange } from '../../Inspector/rsForm';

/** Per-inheritance, per-event action choices: `inheritanceId -> eventId -> action`. */
type InheritanceOptionMap = Record<string, Record<string, string>>;

/**
 * The delete callback: receives the object id and the cascade options, and
 * resolves the delete (report). Forwarded untyped through `useAsynchronousAction`;
 * the leading `id: string` matches the committed caller (`DevEdg.performDelete`).
 */
type PerformDelete = (id: string, ...rest: unknown[]) => Promise<unknown>;

interface DeleteObjectFormProps {
  /** The ModelVersion holding the object type to delete. */
  modelVersion: IModelVersion;
  /** The id of the object type to delete. */
  id: string;
  /** Callback to cancel the deletion. */
  onCancel?: () => void;
  /** Callback that performs the actual delete. */
  onPerformDelete: PerformDelete;
}

const DeleteObjectForm = ({ modelVersion, id, onCancel, onPerformDelete }: DeleteObjectFormProps) => {
  // `content` is the loaded model here (non-null), matching the original JS.
  const object = (modelVersion.content as unknown as IModel).metamodel.getObjectTypeById(id);
  // Object can be undefined for a short while right before unmounting
  const inheritancesAsSupertype = object != null ? object.inheritancesAsSupertype : [];
  const inheritancesAsSubtype = object != null ? object.inheritancesAsSubtype : [];
  const inheritancesToConsider = inheritancesAsSupertype.filter(
    (i) => i.specialisedEvents.length > 0
  );
  const [removeEvents, setRemoveEvents] = useState(true);
  const [inheritanceOptions, setInheritanceOptions] = useState<InheritanceOptionMap>(() => {
    const result: InheritanceOptionMap = {};
    for (const inh of inheritancesToConsider.concat(inheritancesAsSubtype)) {
      // For inheritances as subtype: we always keep. Delete will be determined by 'also remove
      // events' checkbox.
      const options: Record<string, string> = {};
      for (const e of inh.specialisedEvents) {
        options[e.id] = 'KEEP';
      }
      result[inh.id] = options;
    }
    return result;
  });
  const updateInheritanceOptions = (inheritance: string, event: string, value: string) => {
    setInheritanceOptions(
      produce((draft: InheritanceOptionMap) => {
        if (draft[inheritance] == null) {
          draft[inheritance] = {
            [event]: value,
          };
        } else {
          draft[inheritance][event] = value;
        }
      })
    );
  };

  // `onPerformDelete` declares a leading `id: string`; the hook wants the looser
  // `(...args: unknown[]) => Promise<unknown>`. Cast at this boundary only — the
  // same typed args flow through `execute`.
  const deleteAction = useAsynchronousAction(
    onPerformDelete as (...args: unknown[]) => Promise<unknown>
  );
  const busy = deleteAction.busy;
  const valid = inheritancesToConsider.every(
    (inh) =>
      inheritanceOptions[inh.id] != null &&
      inh.specialisedEvents.every((e) => inheritanceOptions[inh.id][e.id] != null)
  );

  return (
    <div className="merlin-modal delete-object-form">
      <div className="title">Remove object type</div>
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid && !busy) {
            deleteAction.execute(id, removeEvents, inheritanceOptions);
          }
        }}
      >
        <div className="modal-body">
          <div className="container">
            <div className="row">
              <div className="col">
                <RSCheckbox
                  name="removeEvents"
                  label="Also remove events that are related to this object type?"
                  checked={removeEvents}
                  onChange={asRSChange<RSCheckboxChange>(
                    (value: string | number, checked: boolean) => setRemoveEvents(checked)
                  )}
                  disabled={busy}
                />
              </div>
            </div>
            {inheritancesToConsider.map((inh, i) => (
              <React.Fragment key={inh.id}>
                <div className="row inheritance-title">
                  <div className="col">Inheritance relation {inh.utf8DisplayString}</div>
                </div>
                {inh.specialisedEvents.map((e, j) => (
                  <SpecialisedEventActionSelector
                    key={e.id}
                    event={e}
                    value={inheritanceOptions[inh.id][e.id]}
                    onChange={(value) => updateInheritanceOptions(inh.id, e.id, value)}
                    style={{ zIndex: 100 - 10 * i - j, position: 'relative' }}
                  />
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
        <div className="button-wrapper">
          <RSButton
            value="Cancel"
            type="button"
            background={Styles.defaultGradient}
            color={Styles.darkTextColor}
            onClick={onCancel}
          />
          <RSButton
            type="submit"
            value={rsValue(busy ? null : 'Remove')}
            background={Styles.blueGradient}
            disabled={!valid || busy}
            iconName={rsValue(busy ? 'FaSpinner' : null)}
            iconSize={15}
            className={rsValue(busy ? 'spin' : null)}
          />
        </div>
      </form>
    </div>
  );
};

export default DeleteObjectForm;
